import { motion, AnimatePresence } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Turnstile } from "@marsidev/react-turnstile";
import { AlertTriangle, X } from "lucide-react";

/** ===================== Config ===================== */
const CREATE_REPORT_URL = "https://whiteintel.io/report_handler_v2.php";
const INSIGHTS_PATH = "/reports";

/** ===================== Component ===================== */
const SearchSection = () => {
  const { t } = useTranslation("home");
  const [turnstileToken, setTurnstileToken] = useState("");
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const turnstileRef = useRef(null);
  const cardRef = useRef(null);

  // Mouse Tracking for Spotlight Effect
  const divRef = useRef(null);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [opacity, setOpacity] = useState(0);

  const handleMouseMove = (e) => {
    if (!divRef.current) return;
    const div = divRef.current;
    const rect = div.getBoundingClientRect();
    setPosition({ x: e.clientX - rect.left, y: e.clientY - rect.top });
  };

  const handleMouseEnter = () => setOpacity(1);
  const handleMouseLeave = () => setOpacity(0);

  useEffect(() => {
    if (err) {
      try {
        cardRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      } catch {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    }
  }, [err]);

  const handleSubmit = async () => {
    setErr("");
    const q = String(query || "").trim();
    if (!q) return setErr(t("search.errorDomain"));
    if (!turnstileToken) return setErr(t("search.errorCaptcha"));

    setLoading(true);
    try {
      const res = await fetch(CREATE_REPORT_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          domain: q,
          "cf-turnstile-response": turnstileToken,
        }),
      });

      const text = await res.text();
      let data;
      try {
        data = JSON.parse(text);
      } catch {
        data = { raw: text };
      }
      if (!res.ok || !data?.uuid) {
        throw new Error(data?.error || `Request failed (${res.status})`);
      }

      const url = `${INSIGHTS_PATH}?uuid=${encodeURIComponent(data.uuid)}`;
      window.location.assign(url);
      return;
    } catch (e) {
      setErr(e.message || "Something went wrong.");
    } finally {
      setLoading(false);
      try {
        turnstileRef.current?.reset();
      } catch {}
      setTurnstileToken("");
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <section className="relative bg-[#020617] text-white px-6 md:px-10 pt-8 sm:pt-10 md:pt-0 pb-12">
      
      {/* Background Glow behind the card (Static Ambience) */}

      <div className="relative max-w-3xl mx-auto mt-0 md:-translate-y-[60%] md:-mb-56 z-10">
        <motion.div
          ref={(node) => {
            cardRef.current = node;
            divRef.current = node;
          }}
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: "easeOut" }}
          onMouseMove={handleMouseMove}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          className="relative group bg-slate-900 border border-white/10 shadow-2xl shadow-black/50 rounded-2xl p-6 sm:p-10 text-center overflow-hidden"
        >
          {/* Spotlight Effect */}
          <div
            className="pointer-events-none absolute -inset-px opacity-0 transition duration-300 z-0"
            style={{
              opacity,
              background: `radial-gradient(600px circle at ${position.x}px ${position.y}px, rgba(99,102,241,0.1), transparent 40%)`,
            }}
          />
          
          {/* Texture */}
          <div 
            className="absolute inset-0 pointer-events-none opacity-[0.15] z-0"
            style={{
                backgroundImage: `radial-gradient(#94a3b8 1px, transparent 1px)`,
                backgroundSize: '24px 24px'
            }}
          />

          {/* Top Edge Highlight */}
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-1/3 h-[1px] bg-gradient-to-r from-transparent via-indigo-400/30 to-transparent z-10"></div>

          <div className="relative z-10">
            <h2 className="text-2xl md:text-2xl font-extrabold mb-3 text-white">
              {t("search.title")}
            </h2>
            <p className="text-slate-200  max-w-2xl mx-auto text-xl">
              {t("search.subtitle")}
            </p>

            <ErrorBanner message={err} onClose={() => setErr("")} />

            <div className="flex flex-col items-center justify-center gap-4 mt-6 mb-6 w-full text-xl">
              <input
                type="text"
                placeholder={t("search.placeholder")}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={onKeyDown}
                className="w-full sm:w-[420px] text-slate-200 px-4 py-3 bg-slate-950/60 border border-slate-700/50 text-white placeholder-slate-500 rounded-lg shadow-inner focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-all z-20"
                disabled={loading}
              />
<button
  onClick={handleSubmit}
  disabled={loading}
  // 1. Added 'whitespace-nowrap' to prevent line breaking
  // 2. Moved 'text-sm font-bold' here so it applies to BOTH states
  className={`w-full sm:w-1/3 whitespace-nowrap text-sm font-bold px-6 py-2.5 rounded-full transition-all text-white shadow-lg relative overflow-hidden group/btn
    ${loading
      ? "bg-[#ff5120]/60 cursor-not-allowed"
      : "bg-gradient-to-r from-[#ff5120] to-[#d9380b] hover:from-[#ff6b42] hover:to-[#e63b0a] shadow-[0_0_20px_-5px_rgba(255,81,32,0.4)] hover:shadow-[0_0_25px_-5px_rgba(255,81,32,0.6)] hover:scale-[1.02] active:scale-[0.98]"
    }`}
>
  <div className="relative z-10 flex items-center justify-center">
    {loading ? (
      // Now this text inherits text-sm and font-bold from the parent button
      t("search.loading")
    ) : (
      <>
        {t("search.button")}{" "}
        <span className="font-bold text-xl leading-none ml-1 group-hover/btn:translate-x-0.5 transition-transform">
          →
        </span>
      </>
    )}
  </div>
</button>
            </div>
           <div className="flex flex-col items-center gap-2 relative z-20">
              <div className={err ? "" : "invisible h-0 overflow-hidden"}>
                <Turnstile
                  siteKey="0x4AAAAAABHcWCVoBFGccBYv"
                  onSuccess={(token) => setTurnstileToken(token)}
                  options={{ theme: "dark", appearance: "always" }}
                  ref={turnstileRef}
                />
              </div>
            </div>
            </div>
        </motion.div>
      </div>
    </section>
  );
};

function ErrorBanner({ message, onClose }) {
  return (
    <AnimatePresence>
      {message ? (
        <motion.div
          key="error-banner"
          initial={{ opacity: 0, y: -6 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -6 }}
          className="mt-4 relative z-20"
        >
          <div className="rounded-lg border border-red-500/20 bg-red-500/10 text-left">
            <div className="px-4 py-3 flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-red-400 mt-[2px]" />
              <div className="flex-1 text-sm text-red-200 leading-5">
                <div className="font-semibold text-red-300">We couldn’t complete your request</div>
                <div className="mt-0.5 opacity-90">{message}</div>
              </div>
              <button
                type="button"
                onClick={onClose}
                aria-label="Dismiss error"
                className="rounded-md border border-red-500/20 bg-black/20 p-1 text-red-400 hover:bg-black/40 hover:text-white transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}

export default SearchSection;